import React, { useState } from 'react';
import './IntegrationModals.css';
import { FaWhatsapp, FaEnvelope, FaInstagram, FaPlug, FaGlobe } from 'react-icons/fa';
import { SiMeta } from 'react-icons/si';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';

const ICON_MAP = {
    whatsapp_evolution: FaWhatsapp,
    whatsapp: FaWhatsapp,
    whatsapp_cloud_api: SiMeta,
    whatsapp_official: SiMeta,
    email_smtp: FaEnvelope,
    email: FaEnvelope,
    instagram: FaInstagram,
    site: FaGlobe,
    web: FaGlobe,
    other: FaPlug,
};


const AddIntegrationModal = ({ isOpen, onClose, providerKey, providerName, providers = [], onCreate }) => {
    const [selectedProvider, setSelectedProvider] = useState(providerKey || '');
    const [label, setLabel] = useState('');
    const [credentials, setCredentials] = useState({
        token: '',
        instance_id: '',
        host: '',
        email_host: '',
        port: 587,
        username: '',
        password: '',
        use_tls: true,
        use_ssl: false,
        sender_name: '',
        // Cloud API credentials
        phone_number_id: '',
        business_account_id: '',
        access_token: '',
        // Site credentials
        webhook_url: '',
        webhook_secret: '',
        site_url: '',
        revalidate_on_publish: true,
        revalidate_on_update: true,
        revalidate_on_delete: true,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const showProviderSelection = !providerKey && providers.length > 0;

    const getSelectedProviderInfo = () => {
        if (providerKey) {
            return { key: providerKey, name: providerName };
        }
        return providers.find(p => p.key === selectedProvider);
    };

    const getProviderIcon = (provider) => {
        const iconKey = provider?.icon_key || provider?.type || provider?.key || "other";
        const Icon = ICON_MAP[iconKey] || ICON_MAP.other;
        return Icon;
    };

    const handleCredentialChange = (e) => {
        const { name, value } = e.target;

        if (name === 'port') {
            setCredentials((prev) => ({
                ...prev,
                port: Number(value) || 0,
            }));
            return;
        }

        if (name === 'use_tls' || name === 'use_ssl') {
            setCredentials((prev) => ({
                ...prev,
                [name]: value === 'true',
            }));
            return;
        }

        // Site integration checkboxes
        if (name === 'revalidate_on_publish' || name === 'revalidate_on_update' || name === 'revalidate_on_delete') {
            setCredentials((prev) => ({
                ...prev,
                [name]: e.target.checked,
            }));
            return;
        }

        setCredentials((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const targetProviderKey = providerKey || selectedProvider;

        if (!targetProviderKey) {
            setError('Por favor, selecione um canal');
            return;
        }

        if (!label.trim()) {
            setError('Por favor, insira um rótulo');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let newIntegration = null;

            try {
                const createRes = await api.post(`/api/integrations/${targetProviderKey}/connect`, {
                    env: 'production',
                });
                newIntegration = createRes.data;


                await api.put(`/api/integrations/${newIntegration.id}`, {
                    display_label: label.trim(),
                });


                let credentialsPayload;

                if (targetProviderKey === 'whatsapp_evolution') {
                    const hasAnyCredential =
                        (credentials.host && credentials.host.trim()) ||
                        (credentials.instance_id && credentials.instance_id.trim()) ||
                        (credentials.token && credentials.token.trim());

                    if (hasAnyCredential) {
                        const missingFields = [];
                        if (!credentials.host || !credentials.host.trim()) missingFields.push('URL da Evolution API');
                        if (!credentials.instance_id || !credentials.instance_id.trim()) missingFields.push('Nome da instância');
                        if (!credentials.token || !credentials.token.trim()) missingFields.push('API Key');

                        if (missingFields.length > 0) {
                            throw new Error(`Para salvar as credenciais do WhatsApp, preencha todos os campos obrigatórios: ${missingFields.join(', ')}`);
                        }

                        credentialsPayload = {
                            credentials: {
                                host: credentials.host.trim(),
                                instance_id: credentials.instance_id.trim(),
                                token: credentials.token.trim(),
                                webhook_url: null,
                            },
                        };
                    }
                } else if (targetProviderKey === 'whatsapp_cloud_api') {
                    const hasAnyCredential =
                        (credentials.phone_number_id && credentials.phone_number_id.trim()) ||
                        (credentials.business_account_id && credentials.business_account_id.trim()) ||
                        (credentials.access_token && credentials.access_token.trim());

                    if (hasAnyCredential) {
                        const missingFields = [];
                        if (!credentials.phone_number_id || !credentials.phone_number_id.trim()) missingFields.push('Phone Number ID');
                        if (!credentials.business_account_id || !credentials.business_account_id.trim()) missingFields.push('Business Account ID');
                        if (!credentials.access_token || !credentials.access_token.trim()) missingFields.push('Access Token');

                        if (missingFields.length > 0) {
                            throw new Error(`Para salvar as credenciais da Cloud API, preencha todos os campos obrigatórios: ${missingFields.join(', ')}`);
                        }

                        credentialsPayload = {
                            credentials: {
                                phone_number_id: credentials.phone_number_id.trim(),
                                business_account_id: credentials.business_account_id.trim(),
                                access_token: credentials.access_token.trim(),
                                // Evolution API fields for compatibility
                                host: credentials.host?.trim() || 'https://graph.facebook.com',
                                instance_id: credentials.phone_number_id.trim(),
                                token: credentials.access_token.trim(),
                            },
                        };
                    }
                } else if (targetProviderKey === 'email_smtp') {
                    const hasAnyCredential =
                        (credentials.email_host && credentials.email_host.trim()) ||
                        (credentials.username && credentials.username.trim()) ||
                        (credentials.password && credentials.password.trim());

                    if (hasAnyCredential) {

                        const missingFields = [];
                        if (!credentials.email_host || !credentials.email_host.trim()) missingFields.push('Mail Server (Host)');
                        if (!credentials.username || !credentials.username.trim()) missingFields.push('Usuário');
                        if (!credentials.password || !credentials.password.trim()) missingFields.push('Senha');

                        if (missingFields.length > 0) {
                            throw new Error(`Para salvar as credenciais de Email, preencha todos os campos obrigatórios: ${missingFields.join(', ')}`);
                        }

                        credentialsPayload = {
                            credentials: {
                                host: credentials.email_host.trim(),
                                port: Number(credentials.port) || 587,
                                username: credentials.username.trim(),
                                password: credentials.password.trim(),
                                use_tls: credentials.use_tls,
                                use_ssl: credentials.use_ssl,
                                sender_name: credentials.sender_name && credentials.sender_name.trim() ? credentials.sender_name.trim() : null,
                            },
                        };
                    }
                } else if (targetProviderKey === 'site') {
                    const hasAnyCredential =
                        (credentials.webhook_url && credentials.webhook_url.trim()) ||
                        (credentials.webhook_secret && credentials.webhook_secret.trim());

                    if (hasAnyCredential) {
                        const missingFields = [];
                        if (!credentials.webhook_url || !credentials.webhook_url.trim()) missingFields.push('URL do Webhook');
                        if (!credentials.webhook_secret || !credentials.webhook_secret.trim()) missingFields.push('Secret do Webhook');

                        if (missingFields.length > 0) {
                            throw new Error(`Para salvar as credenciais do Site, preencha todos os campos obrigatórios: ${missingFields.join(', ')}`);
                        }

                        credentialsPayload = {
                            credentials: {
                                webhook_url: credentials.webhook_url.trim(),
                                webhook_secret: credentials.webhook_secret.trim(),
                                site_url: credentials.site_url?.trim() || null,
                                revalidate_on_publish: credentials.revalidate_on_publish,
                                revalidate_on_update: credentials.revalidate_on_update,
                                revalidate_on_delete: credentials.revalidate_on_delete,
                            },
                        };
                    }
                }

                if (credentialsPayload) {
                    await api.put(`/api/integrations/${newIntegration.id}`, credentialsPayload);
                }

                toast.success(`Canal "${label}" criado com sucesso!`);

                setLabel('');
                setSelectedProvider('');
                setCredentials({
                    token: '',
                    instance_id: '',
                    host: '',
                    email_host: '',
                    port: 587,
                    username: '',
                    password: '',
                    use_tls: true,
                    use_ssl: false,
                    sender_name: '',
                    phone_number_id: '',
                    business_account_id: '',
                    access_token: '',
                    webhook_url: '',
                    webhook_secret: '',
                    site_url: '',
                    revalidate_on_publish: true,
                    revalidate_on_update: true,
                    revalidate_on_delete: true,
                });

                if (typeof onCreate === 'function') {
                    onCreate();
                }

                onClose();
            } catch (innerError) {
                if (newIntegration && newIntegration.id) {
                    try {
                        await api.delete(`/api/integrations/${newIntegration.id}`);
                    } catch (deleteError) {
                    }
                }
                throw innerError;
            }
        } catch (err) {

            let errorMessage = 'Falha ao criar integração';

            if (err.response?.data?.detail) {
                const detail = err.response.data.detail;

                if (Array.isArray(detail)) {
                    const validationErrors = detail.map(error => {
                        const field = error.loc ? error.loc[error.loc.length - 1] : 'campo';
                        const fieldNames = {
                            'host': 'URL/Host',
                            'instance_id': 'Nome da instância',
                            'token': 'API Key',
                            'username': 'Usuário',
                            'password': 'Senha',
                            'port': 'Porta'
                        };
                        const friendlyField = fieldNames[field] || field;

                        if (error.type === 'url_parsing') {
                            return `${friendlyField}: URL inválida. Use um formato válido como http://exemplo.com`;
                        } else if (error.type === 'string_too_short' || error.type === 'missing') {
                            return `${friendlyField}: campo obrigatório não preenchido`;
                        } else if (error.type === 'value_error') {
                            return `${friendlyField}: valor inválido`;
                        } else {
                            return `${friendlyField}: ${error.msg || 'erro de validação'}`;
                        }
                    });
                    errorMessage = validationErrors.join('\n');
                } else if (typeof detail === 'string') {
                    if (detail.includes('url_parsing') || detail.includes('Input should be a valid URL')) {
                        errorMessage = 'URL inválida. Por favor, insira uma URL válida no formato: http://exemplo.com ou https://exemplo.com';
                    } else if (detail.includes('Field required') || detail.includes('missing')) {
                        errorMessage = 'Alguns campos obrigatórios não foram preenchidos corretamente';
                    } else if (detail.includes('validation error')) {
                        errorMessage = 'Erro de validação. Verifique se todos os campos estão preenchidos corretamente';
                    } else {
                        errorMessage = detail;
                    }
                }
            } else if (err.response?.data?.error) {
                const error = err.response.data.error;


                if (typeof error === 'string' && (error.includes('url_parsing') || error.includes('Input should be a valid URL'))) {
                    errorMessage = 'URL inválida. Por favor, insira uma URL válida no formato: http://exemplo.com ou https://exemplo.com';
                } else if (typeof error === 'string' && (error.includes('Field required') || error.includes('missing'))) {
                    errorMessage = 'Alguns campos obrigatórios não foram preenchidos corretamente';
                } else if (typeof error === 'string' && error.includes('validation error')) {
                    errorMessage = 'Erro de validação. Verifique se todos os campos estão preenchidos corretamente';
                } else {
                    errorMessage = error;
                }
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                if (err.message.includes('url_parsing') || err.message.includes('Input should be a valid URL')) {
                    errorMessage = 'URL inválida. Por favor, insira uma URL válida no formato: http://exemplo.com ou https://exemplo.com';
                } else {
                    errorMessage = `Erro: ${err.message}`;
                }
            }

            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const renderCredentialFields = () => {
        const targetProviderKey = providerKey || selectedProvider;

        if (!targetProviderKey) return null;

        if (targetProviderKey === 'whatsapp_evolution') {
            return (
                <>
                    <h4 className="integration-section-title">Credenciais do WhatsApp</h4>

                    <div className="integration-form-group">
                        <label htmlFor="token">API Key</label>
                        <input
                            id="token"
                            name="token"
                            type="text"
                            className="integration-input"
                            value={credentials.token}
                            onChange={handleCredentialChange}
                            placeholder="Insira sua API Key aqui"
                            disabled={loading}
                        />
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="instance_id">Nome da instância</label>
                        <input
                            id="instance_id"
                            name="instance_id"
                            type="text"
                            className="integration-input"
                            value={credentials.instance_id}
                            onChange={handleCredentialChange}
                            placeholder="Ex: fastteam_main_instance"
                            disabled={loading}
                        />
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="host">URL da Evolution API</label>
                        <input
                            id="host"
                            name="host"
                            type="text"
                            className="integration-input"
                            value={credentials.host}
                            onChange={handleCredentialChange}
                            placeholder="Ex: http://evolution-api:8080"
                            disabled={loading}
                        />
                    </div>
                </>
            );
        }

        if (targetProviderKey === 'whatsapp_cloud_api') {
            return (
                <>
                    <h4 className="integration-section-title">Credenciais da API Oficial do WhatsApp</h4>

                    <div className="cloud-api-info-box">
                        <p>
                            Para usar a API Oficial do WhatsApp, você precisa ter uma conta Meta Business verificada e
                            acesso ao WhatsApp Business Platform. As credenciais podem ser obtidas no{' '}
                            <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer">
                                Meta for Developers
                            </a>.
                        </p>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="phone_number_id">Phone Number ID *</label>
                        <input
                            id="phone_number_id"
                            name="phone_number_id"
                            type="text"
                            className="integration-input"
                            value={credentials.phone_number_id}
                            onChange={handleCredentialChange}
                            placeholder="Ex: 123456789012345"
                            disabled={loading}
                        />
                        <small className="integration-form-hint">
                            ID do número de telefone registrado no WhatsApp Business
                        </small>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="business_account_id">WhatsApp Business Account ID (WABA) *</label>
                        <input
                            id="business_account_id"
                            name="business_account_id"
                            type="text"
                            className="integration-input"
                            value={credentials.business_account_id}
                            onChange={handleCredentialChange}
                            placeholder="Ex: 987654321098765"
                            disabled={loading}
                        />
                        <small className="integration-form-hint">
                            ID da conta WhatsApp Business (encontrado nas configurações do Meta Business)
                        </small>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="access_token">Access Token (Permanente) *</label>
                        <input
                            id="access_token"
                            name="access_token"
                            type="password"
                            className="integration-input"
                            value={credentials.access_token}
                            onChange={handleCredentialChange}
                            placeholder="EAAxxxxxxxxxxxxxxx..."
                            disabled={loading}
                        />
                        <small className="integration-form-hint">
                            Token de acesso permanente gerado no Meta for Developers
                        </small>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="host">URL do Evolution API (opcional)</label>
                        <input
                            id="host"
                            name="host"
                            type="text"
                            className="integration-input"
                            value={credentials.host}
                            onChange={handleCredentialChange}
                            placeholder="Ex: http://evolution-api:8080"
                            disabled={loading}
                        />
                        <small className="integration-form-hint">
                            Se usando Evolution API como proxy para Cloud API
                        </small>
                    </div>
                </>
            );
        }

        if (targetProviderKey === 'email_smtp') {
            return (
                <>
                    <h4 className="integration-section-title">Credenciais de Email (SMTP)</h4>

                    <div className="integration-form-group">
                        <label htmlFor="email_host">Mail Server (Host)</label>
                        <input
                            id="email_host"
                            name="email_host"
                            type="text"
                            className="integration-input"
                            value={credentials.email_host}
                            onChange={handleCredentialChange}
                            placeholder="Ex: smtp.gmail.com"
                            disabled={loading}
                        />
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="port">Porta</label>
                        <input
                            id="port"
                            name="port"
                            type="number"
                            className="integration-input"
                            value={credentials.port}
                            onChange={handleCredentialChange}
                            placeholder="Ex: 587 ou 465"
                            disabled={loading}
                        />
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="use_tls">Usar TLS</label>
                        <select
                            id="use_tls"
                            name="use_tls"
                            className="integration-input"
                            value={credentials.use_tls ? 'true' : 'false'}
                            onChange={handleCredentialChange}
                            disabled={loading}
                        >
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="use_ssl">Usar SSL</label>
                        <select
                            id="use_ssl"
                            name="use_ssl"
                            className="integration-input"
                            value={credentials.use_ssl ? 'true' : 'false'}
                            onChange={handleCredentialChange}
                            disabled={loading}
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="username">Usuário (login)</label>
                        <input
                            id="username"
                            name="username"
                            type="email"
                            className="integration-input"
                            value={credentials.username}
                            onChange={handleCredentialChange}
                            placeholder="Seu usuário de e-mail / SMTP"
                            disabled={loading}
                        />
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="password">Senha / App Password</label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            className="integration-input"
                            value={credentials.password}
                            onChange={handleCredentialChange}
                            placeholder="Senha do SMTP ou App Password"
                            disabled={loading}
                        />
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="sender_name">Nome do remetente (opcional)</label>
                        <input
                            id="sender_name"
                            name="sender_name"
                            type="text"
                            className="integration-input"
                            value={credentials.sender_name}
                            onChange={handleCredentialChange}
                            placeholder="Ex: Equipe FASTTEAM"
                            disabled={loading}
                        />
                    </div>
                </>
            );
        }

        if (targetProviderKey === 'site') {
            const generateSecret = () => {
                const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                let secret = '';
                for (let i = 0; i < 32; i++) {
                    secret += chars.charAt(Math.floor(Math.random() * chars.length));
                }
                setCredentials((prev) => ({ ...prev, webhook_secret: secret }));
            };

            return (
                <>
                    <h4 className="integration-section-title">Configuração do Website</h4>

                    <div className="integration-info-box">
                        <p>
                            Configure seu site (Next.js, React, etc.) para receber atualizações automáticas
                            do CMS. Quando você publicar, atualizar ou remover conteúdo, o FastTeam enviará
                            um webhook para seu site revalidar as páginas.
                        </p>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="webhook_url">URL do Webhook *</label>
                        <input
                            id="webhook_url"
                            name="webhook_url"
                            type="url"
                            className="integration-input"
                            value={credentials.webhook_url}
                            onChange={handleCredentialChange}
                            placeholder="https://meusite.com/api/revalidate"
                            disabled={loading}
                        />
                        <small className="integration-form-hint">
                            Endpoint do seu site que receberá as notificações de conteúdo
                        </small>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="webhook_secret">Secret do Webhook *</label>
                        <div className="input-with-button">
                            <input
                                id="webhook_secret"
                                name="webhook_secret"
                                type="text"
                                className="integration-input"
                                value={credentials.webhook_secret}
                                onChange={handleCredentialChange}
                                placeholder="Chave secreta para autenticação"
                                disabled={loading}
                                minLength={8}
                            />
                            <button
                                type="button"
                                className="generate-btn"
                                onClick={generateSecret}
                                disabled={loading}
                            >
                                Gerar
                            </button>
                        </div>
                        <small className="integration-form-hint">
                            Use esta mesma chave no seu site (env: CMS_WEBHOOK_SECRET) para validar os webhooks
                        </small>
                    </div>

                    <div className="integration-form-group">
                        <label htmlFor="site_url">URL do Site (opcional)</label>
                        <input
                            id="site_url"
                            name="site_url"
                            type="url"
                            className="integration-input"
                            value={credentials.site_url}
                            onChange={handleCredentialChange}
                            placeholder="https://meusite.com"
                            disabled={loading}
                        />
                        <small className="integration-form-hint">
                            URL pública do seu site (usado para preview de conteúdo)
                        </small>
                    </div>

                    <div className="integration-form-section">
                        <h4 className="integration-section-title">Quando enviar webhooks?</h4>

                        <div className="integration-checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="revalidate_on_publish"
                                    checked={credentials.revalidate_on_publish}
                                    onChange={handleCredentialChange}
                                    disabled={loading}
                                />
                                <span className="checkbox-text">
                                    <strong>Ao publicar conteúdo</strong>
                                    <small>Notificar quando páginas ou posts forem publicados</small>
                                </span>
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="revalidate_on_update"
                                    checked={credentials.revalidate_on_update}
                                    onChange={handleCredentialChange}
                                    disabled={loading}
                                />
                                <span className="checkbox-text">
                                    <strong>Ao atualizar conteúdo</strong>
                                    <small>Notificar quando conteúdo publicado for editado</small>
                                </span>
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="revalidate_on_delete"
                                    checked={credentials.revalidate_on_delete}
                                    onChange={handleCredentialChange}
                                    disabled={loading}
                                />
                                <span className="checkbox-text">
                                    <strong>Ao remover conteúdo</strong>
                                    <small>Notificar quando conteúdo for deletado ou despublicado</small>
                                </span>
                            </label>
                        </div>
                    </div>
                </>
            );
        }

        return null;
    };

    if (!isOpen) return null;

    const selectedProviderInfo = getSelectedProviderInfo();

    return (
        <div className="integration-modal-overlay" onClick={onClose}>
            <div className="integration-modal integration-modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="integration-modal-header">
                    <h2>
                        {showProviderSelection
                            ? 'Adicionar Novo Canal'
                            : `Adicionar ${selectedProviderInfo?.name || 'Canal'}`}
                    </h2>
                    <button className="integration-modal-close" onClick={onClose} disabled={loading}>×</button>
                </div>

                <form onSubmit={handleSubmit} className="integration-modal-form">
                    <div className="integration-modal-body">
                        {showProviderSelection && (
                            <div className="integration-form-group">
                                <label htmlFor="provider-select">Escolha o Canal *</label>
                                <div className="integration-provider-grid">
                                    {providers
                                        .filter(p => p.key !== 'instagram')
                                        .map((provider) => {
                                            const Icon = getProviderIcon(provider);
                                            return (
                                                <button
                                                    key={provider.key}
                                                    type="button"
                                                    className={`integration-provider-card ${selectedProvider === provider.key ? 'selected' : ''}`}
                                                    onClick={() => setSelectedProvider(provider.key)}
                                                    disabled={loading}
                                                >
                                                    <Icon className="integration-provider-icon" />
                                                    <span>{provider.name}</span>
                                                </button>
                                            );
                                        })}
                                </div>
                            </div>
                        )}

                        <p className="integration-modal-description">
                            Dê um rótulo personalizado para esta integração e configure as credenciais.
                        </p>

                        <div className="integration-form-group">
                            <label htmlFor="integration-label">Nome da Integração *</label>
                            <input
                                id="integration-label"
                                type="text"
                                className="integration-input"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="Ex: Gmail Vendas, WhatsApp Suporte"
                                autoFocus={!showProviderSelection}
                                disabled={loading}
                                required
                            />
                        </div>

                        {renderCredentialFields()}

                        {error && (
                            <div className="integration-error-message">
                                {error}
                            </div>
                        )}
                    </div>

                    <div className="integration-modal-footer">
                        <button
                            type="button"
                            className="integration-btn integration-btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="integration-btn integration-btn-primary"
                            disabled={loading || !label.trim() || (!providerKey && !selectedProvider)}
                        >
                            {loading ? 'Criando...' : 'Criar Canal'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddIntegrationModal;
